import Choices from 'choices.js';

class App {
  constructor() {
    this.init();
  }

  init() {
    this.initNavigation();
    this.initProductGallery();
    this.initWishlist();
    this.initModals();
    this.initSearch();
    this.initSelects();
    this.initProductFilters();
    this.initProductHoverSlider();
  }

  // Navigation
  initNavigation() {
    this.initMobileNav();
    this.initDesktopNav();
  }

  initMobileNav() {
    const navToggle = document.querySelector('.js-nav-toggle');
    const navMenu = document.querySelector('.js-nav ul');
    const html = document.documentElement;
    const body = document.body;

    if (!navToggle || !navMenu) return;

    const toggleMobileMenu = () => {
      const isActive = navToggle.classList.contains('is-active');
      navToggle.classList.toggle('is-active');
      navMenu.classList.toggle('is-active');

      // Toggle scroll lock
      if (!isActive) {
        // Lock scroll
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
      } else {
        // Unlock scroll
        html.style.overflow = '';
        body.style.overflow = '';
      }
    };

    const closeMobileMenu = (e) => {
      if (!navMenu.contains(e.target) && !navToggle.contains(e.target)) {
        navToggle.classList.remove('is-active');
        navMenu.classList.remove('is-active');
        // Unlock scroll
        html.style.overflow = '';
        body.style.overflow = '';
      }
    };

    navToggle.addEventListener('click', toggleMobileMenu);
    document.addEventListener('click', closeMobileMenu);
  }

  initDesktopNav() {
    const dropdownTriggers = document.querySelectorAll('.js-nav-dropdown-open');
    const navItems = document.querySelectorAll('.js-nav-item');

    if (!dropdownTriggers.length) return;

    const closeAllDropdowns = () => {
      dropdownTriggers.forEach(trigger => trigger.classList.remove('is-open'));
    };

    const handleDropdownTrigger = (event) => {
      const target = event.target.closest('.js-nav-dropdown-open') || event.target;
      
      if (!target.closest('a') && !target.closest('button')) {
        event.preventDefault();
        closeAllDropdowns();
        
        // const parent = target.parentElement;
        // parent.style.setProperty('--offset-left', `${target.offsetLeft}px`);
        target.closest('.js-nav-dropdown-open').classList.toggle('is-open');
      }
    };

    const handleNavItemHover = () => {
      closeAllDropdowns();
    };

    const handleClickOutside = (event) => {
      const nav = document.querySelector('.js-nav');
      if (nav && !nav.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    const handleMouseOut = (event) => {
      const nav = document.querySelector('.js-nav');
      if (nav && !nav.contains(event.target) && !nav.contains(event.relatedTarget)) {
        closeAllDropdowns();
      }
    };

    dropdownTriggers.forEach(trigger => {
      trigger.addEventListener('mouseover', handleDropdownTrigger);
    });

    navItems.forEach(item => {
      item.addEventListener('mouseover', handleNavItemHover);
    });

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mouseout', handleMouseOut);
  }

  initProductGallery() {
    const gallery = document.querySelector('.product-info__gallery');
    if (!gallery) return;

    const mainImage = gallery.querySelector('.js-gallery-main');
    const thumbnails = gallery.querySelectorAll('.js-gallery-thumbnail');
    const thumbnailsContainer = gallery.querySelector('.product-info__gallery-thumbnails');
    const prevButton = gallery.querySelector('.js-gallery-prev');
    const nextButton = gallery.querySelector('.js-gallery-next');
    let currentIndex = 0;
    let isTransitioning = false;
    let hoverTimeout = null;

    const updateGallery = (index) => {
      if (isTransitioning || index === currentIndex) return;
      
      isTransitioning = true;
      
      // Update main image with dissolve effect
      const targetThumb = thumbnails[index];
      const newSrc = targetThumb.src.replace('thumbnail', 'full');
      
      // Start fade out
      mainImage.style.opacity = '0';
      
      // After fade out completes, change image and fade in
      setTimeout(() => {
        mainImage.src = newSrc;
        mainImage.style.opacity = '1';
        
        // Reset transition flag after fade in completes
        setTimeout(() => {
          isTransitioning = false;
        }, 200);
      }, 200);

      // Update active thumbnail
      thumbnails.forEach(thumb => thumb.classList.remove('active'));
      targetThumb.classList.add('active');

      currentIndex = index;
    };

    // Drag to scroll functionality for thumbnails
    if (thumbnailsContainer) {
      let isDown = false;
      let startY;
      let scrollTop;

      thumbnailsContainer.addEventListener('mousedown', (e) => {
        isDown = true;
        thumbnailsContainer.style.cursor = 'grabbing';
        startY = e.pageY - thumbnailsContainer.offsetTop;
        scrollTop = thumbnailsContainer.scrollTop;
        e.preventDefault(); // Prevent text selection
      });

      thumbnailsContainer.addEventListener('mouseleave', () => {
        isDown = false;
        thumbnailsContainer.style.cursor = 'grab';
      });

      thumbnailsContainer.addEventListener('mouseup', () => {
        isDown = false;
        thumbnailsContainer.style.cursor = 'grab';
      });

      thumbnailsContainer.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const y = e.pageY - thumbnailsContainer.offsetTop;
        const walk = (y - startY) * 2; // Scroll speed multiplier
        thumbnailsContainer.scrollTop = scrollTop - walk;
      });

      // Set initial cursor style
      thumbnailsContainer.style.cursor = 'grab';
    }

    // Thumbnail handling with debounce for hover
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('mouseover', () => {
        clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(() => {
          updateGallery(index);
        }, 150);
      });
      
      thumb.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
      });
    });

    // Arrow navigation
    prevButton.addEventListener('click', () => {
      const newIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
      updateGallery(newIndex);
    });

    nextButton.addEventListener('click', () => {
      const newIndex = (currentIndex + 1) % thumbnails.length;
      updateGallery(newIndex);
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!gallery.contains(document.activeElement)) return;
      
      if (e.key === 'ArrowLeft') {
        const newIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
        updateGallery(newIndex);
      } else if (e.key === 'ArrowRight') {
        const newIndex = (currentIndex + 1) % thumbnails.length;
        updateGallery(newIndex);
      }
    });
  }

  // Wishlist
  initWishlist() {
    const STORAGE_KEY = 'cdny_wishlist';
    this.wishlist = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    const productLikes = document.querySelectorAll('.js-product-like');

    if (!productLikes.length) return;

    const toggleWishlist = (event, productId) => {
      event.preventDefault();
      const isActive = event.target.classList.contains('active');
      
      if (!isActive) {
        this.wishlist.push(productId);
        event.target.classList.add('active');
        productLikes.forEach(item => item.dataset.productId === productId && item.classList.add('active'));
      } else {
        const index = this.wishlist.indexOf(productId);
        if (index > -1) this.wishlist.splice(index, 1);
        event.target.classList.remove('active');
        productLikes.forEach(item => item.dataset.productId === productId && item.classList.remove('active'));
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.wishlist));
    };

    productLikes.forEach(item => {
      const productId = item.dataset.productId;
      
      if (this.wishlist.includes(productId)) {
        item.classList.add('active');
      }
      
      item.addEventListener('click', (event) => toggleWishlist(event, productId));
    });
  }

  // Modals
  initModals() {
    this.modals = document.querySelectorAll('.js-modal');
    if (!this.modals.length) return;

    const closeModal = (modal) => {
      modal.classList.remove('open');
    };

    this.modals.forEach(modal => {
      const closeButton = modal.querySelector('.js-modal-close');
      if (!closeButton) return;

      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        closeModal(modal);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.modals.forEach(modal => closeModal(modal));
      }
    });
  }

  // Search
  initSearch() {
    const searchOpen = document.querySelector('.js-search-open');
    if (!searchOpen) return;

    const searchButton = searchOpen.querySelector('button');
    if (!searchButton) return;

    searchButton.addEventListener('click', (event) => {
      if (!searchOpen.classList.contains('active')) {
        event.preventDefault();
        searchOpen.classList.add('active');
      }
    });
  }

  // Selects
  initSelects() {
    const selects = document.querySelectorAll('.js-select');
    if (!selects.length) return;

    this.choices = Array.from(selects).map(select => {
      return new Choices(select, {
        searchEnabled: false,
        itemSelectText: false,
      });
    });
  }

  // Product Filters
  initProductFilters() {
    const productFilters = document.querySelectorAll('.js-product-filter');
    if (!productFilters.length) return;

    const productsSection = document.querySelector('.products[data-page-type]');
    if (!productsSection) return;

    const pageType = productsSection.dataset.pageType;
    const pageId = productsSection.dataset.pageId || 0;

    productFilters.forEach(filter => {
      // Wait for Choices.js to initialize
      setTimeout(() => {
        const choicesInstance = this.choices?.find(choice => choice.passedElement.element === filter);
        
        if (choicesInstance) {
          choicesInstance.passedElement.element.addEventListener('change', () => {
            this.filterProducts(pageType, pageId);
          });
        }
      }, 100);
    });
  }

  // Handle AJAX product filtering
  filterProducts(pageType, pageId) {
    const allFilters = document.querySelectorAll('.js-product-filter');
    const productsGrid = document.querySelector('.products-grid');
    const productsCount = document.querySelector('.products-filters-count');
    
    if (!productsGrid || !productsCount) return;

    // Build filter data
    const filterData = {
      action: 'filter_products',
      page_type: pageType,
      page_id: pageId,
      nonce: cdny_ajax.nonce
    };

    // Get all current filter values
    allFilters.forEach(filterEl => {
      const filterName = filterEl.name;
      const filterValue = filterEl.value;
      
      if (filterValue && filterValue !== '') {
        filterData[filterName] = filterValue;
      }
    });

    // Show loading state
    productsGrid.style.opacity = '0.5';
    productsGrid.style.pointerEvents = 'none';

    // Make AJAX request
    fetch(cdny_ajax.ajax_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(filterData)
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Update products grid
        productsGrid.innerHTML = data.data.products_html;
        
        // Update products count
        productsCount.textContent = `${data.data.products_count} products`;
        
        // Re-initialize wishlist for new products
        this.initWishlist();
      } else {
        console.error('Filter error:', data.data);
      }
    })
    .catch(error => {
      console.error('AJAX error:', error);
    })
    .finally(() => {
      // Remove loading state
      productsGrid.style.opacity = '';
      productsGrid.style.pointerEvents = '';
    });
  }

  // Product Hover Slider
  initProductHoverSlider() {
    const products = document.querySelectorAll('.products--with-hover .product');
    if (!products.length) return;

    products.forEach(product => {
      const slider = product.querySelector('.product-hover-gallery-slider');
      if (!slider) return;

      const track = slider.querySelector('.product-hover-gallery-track');
      const slides = slider.querySelectorAll('.product-hover-gallery-slide');
      const prevButton = slider.querySelector('.product-hover-gallery-prev');
      const nextButton = slider.querySelector('.product-hover-gallery-next');
      
      let currentIndex = 0;
      let autoSlideInterval;

      // Hide navigation if there's only one image
      if (!slides.length || slides.length <= 1) {
        if (prevButton) prevButton.style.display = 'none';
        if (nextButton) nextButton.style.display = 'none';
      } else {
        // Show navigation for multiple images
        if (prevButton) prevButton.style.display = 'flex';
        if (nextButton) nextButton.style.display = 'flex';

        const updateSlider = (index) => {
          slides.forEach((slide, i) => {
            slide.classList.toggle('active', i === index);
          });
          currentIndex = index;
        };

        const nextSlide = () => {
          const newIndex = (currentIndex + 1) % slides.length;
          updateSlider(newIndex);
        };

        const prevSlide = () => {
          const newIndex = (currentIndex - 1 + slides.length) % slides.length;
          updateSlider(newIndex);
        };

        // Event listeners for navigation
        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            nextSlide();
          });
        }

        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            prevSlide();
          });
        }

        // Auto-slide on hover for multiple images
        product.addEventListener('mouseenter', () => {
          if (slides.length > 1) {
            autoSlideInterval = setInterval(nextSlide, 2000); // Change image every 2 seconds
          }
        });

        product.addEventListener('mouseleave', () => {
          if (autoSlideInterval) {
            clearInterval(autoSlideInterval);
            autoSlideInterval = null;
          }
          // Reset to first slide when leaving
          updateSlider(0);
        });
      }

      // Handle "VIEW MORE DETAILS" button click
      const detailsButton = product.querySelector('.product-hover-details');
      if (detailsButton) {
        detailsButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Redirect to product page
          window.location.href = product.href;
        });
      }
    });
  }
}

// Initialize app when DOM is ready
if (document.readyState !== 'loading') {
  new App();
} else {
  document.addEventListener('DOMContentLoaded', () => new App());
}
